/**
 * utils/syncProviderUtils.ts
 * Utility functions for sync providers
 *
 * Centralizes provider-related logic that doesn't belong in the provider
 * implementations themselves (display names, validation, etc.)
 */

import type { ProviderKey } from '@/types/sync';
import type { SyncProvider, SyncProviderType } from '@/types/sync/SyncProvider';

/**
 * Check if a provider requires internet connectivity
 *
 * Cloud providers (Google Drive, Dropbox, OneDrive, Box) require network
 * connectivity to function. Local Directory uses File System API which
 * works offline.
 *
 * This utility is used to determine:
 * - Whether to auto-retry connection on 'online' events
 * - Whether to block connection attempts when offline
 * - Whether to show connectivity-related error messages
 *
 * @param providerKey - Provider identifier
 * @returns true if provider requires connectivity, false otherwise
 *
 * @example
 * ```ts
 * // Auto-retry only for cloud providers
 * if (requiresConnectivity('google-drive')) {
 *   window.addEventListener('online', handleAutoRetry);
 * }
 * ```
 *
 * @example
 * ```ts
 * // Block offline connections for cloud providers
 * if (!isOnline() && requiresConnectivity(provider)) {
 *   showOfflineError();
 *   return;
 * }
 * ```
 */
export function requiresConnectivity(providerKey: ProviderKey): boolean {
  // Local Directory uses File System API which works offline
  // All other providers require network connectivity
  return providerKey !== 'local';
}

/**
 * Get user-friendly display name for provider type
 *
 * Maps internal provider type identifiers to human-readable names
 * for use in UI (toasts, modals, settings, etc.)
 *
 * Centralized mapping ensures consistency across:
 * - Toast messages
 * - Modal titles
 * - Settings UI
 * - Error messages
 * - Logs (when appropriate)
 *
 * @param type - Provider type identifier
 * @returns Human-readable provider name
 *
 * @example
 * ```ts
 * const displayName = getProviderDisplayName('google-drive');
 * console.log(`Connected to ${displayName}`); // "Connected to Google Drive"
 * ```
 *
 * @example Usage in UI
 * ```tsx
 * <Toast>
 *   {getProviderDisplayName(provider)} connection expired
 * </Toast>
 * ```
 */
export function getProviderDisplayName(type: SyncProviderType): string {
  switch (type) {
    case 'google-drive':
      return 'Google Drive';

    case 'local':
      return 'Local Directory';

    case 'dropbox':
      return 'Dropbox';

    case 'onedrive':
      return 'OneDrive';

    case 'box':
      return 'Box';

    // Fallback for unknown provider types (should never happen with proper typing)
    default:
      // Return type identifier as-is (better than empty string)
      return type;
  }
}

/**
 * Type for a SyncProvider with guaranteed binary support
 *
 * Use this type after calling assertBinarySupport() to get
 * type-safe access to binary methods without optional chaining.
 *
 * Includes `supportsBinary: true` to fully reflect the runtime assertion.
 */
export type BinarySupportedProvider = SyncProvider & {
  readonly supportsBinary: true;
} & Required<Pick<SyncProvider, 'readBinary' | 'writeBinary' | 'deleteBinary' | 'existsBinary'>>;

/**
 * Assert that a provider supports binary operations
 *
 * Validates that the provider has both the supportsBinary flag set to true
 * AND all four binary methods implemented. This prevents runtime errors
 * from providers that set the flag without implementing the methods.
 *
 * After calling this function, TypeScript narrows the provider type to
 * include all binary methods as required (non-optional).
 *
 * @param provider - The sync provider to validate
 * @throws Error if provider doesn't support binary operations
 *
 * @example
 * ```ts
 * // Before: binary methods are optional
 * const provider: SyncProvider = getProvider();
 *
 * // Assert binary support (throws if not supported)
 * assertBinarySupport(provider);
 *
 * // After: binary methods are guaranteed to exist
 * const data = await provider.readBinary('image.png');
 * await provider.writeBinary('image.png', data);
 * ```
 */
export function assertBinarySupport(
  provider: SyncProvider
): asserts provider is BinarySupportedProvider {
  if (
    !provider.supportsBinary ||
    !provider.readBinary ||
    !provider.writeBinary ||
    !provider.deleteBinary ||
    !provider.existsBinary
  ) {
    throw new Error(
      `Provider "${provider.name}" does not support binary operations. ` +
        'Ensure supportsBinary is true and all binary methods are implemented.'
    );
  }
}
